import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useExperimentalStore } from '../../store/experimentalStore';
import { useProductStore } from '../../store/productStore';
import clsx from 'clsx';
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Calculator,
  TrendingUp,
  BarChart3,
  CalendarDays,
  FileText,
  LineChart,
  ClipboardCheck,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  FlaskConical,
  Brain,
  Shield,
  Settings as SettingsIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import PriceTicker from '../ui/PriceTicker';
import ChatBot from '../ui/ChatBot';
// ProductSwitcher hidden until Beef View is ready for production
// import ProductSwitcher from '../ui/ProductSwitcher';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  roles?: string[];
  experimental?: boolean;
}

const grainNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'manager'] },
  { name: 'My Marketing Plan', href: '/my-plan', icon: BarChart3, roles: ['client'] },
  { name: 'Clients', href: '/clients', icon: Users, roles: ['admin', 'manager'] },
  { name: 'Questionnaires', href: '/questionnaires', icon: ClipboardCheck, roles: ['admin', 'manager'] },
  { name: 'Breakeven', href: '/calculators/breakeven', icon: Calculator, roles: ['admin', 'manager'] },
  { name: 'Marketing Plan', href: '/marketing-plan', icon: BarChart3, roles: ['admin', 'manager'] },
  { name: 'Orders', href: '/orders', icon: ClipboardList, roles: ['admin', 'manager'] },
  { name: 'Profitability', href: '/calculators/profitability', icon: TrendingUp, roles: ['admin', 'manager'] },
  { name: 'Options', href: '/options', icon: LineChart, roles: ['admin', 'manager'] },
  { name: 'Deliveries', href: '/deliveries', icon: CalendarDays, roles: ['admin', 'manager'], experimental: true },
  { name: 'Marketing AI', href: '/marketing-ai', icon: Brain, roles: ['admin', 'manager'], experimental: true },
  { name: 'Yield Matrix', href: '/insurance-yield', icon: Shield, roles: ['admin', 'manager'], experimental: true },
  { name: 'Reports', href: '/reports', icon: FileText, roles: ['admin', 'manager'] },
  { name: 'Settings', href: '/settings', icon: SettingsIcon },
];

const beefNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Endorsements', href: '/beef/endorsements', icon: FileText, roles: ['admin', 'manager'] },
  { name: 'Insureds', href: '/beef/insureds', icon: Users, roles: ['admin', 'manager'] },
  { name: 'LRP Quotes', href: '/beef/quotes', icon: TrendingUp, roles: ['admin', 'manager'] },
  { name: 'Break-Even', href: '/beef/breakeven', icon: Calculator, roles: ['admin', 'manager'] },
  { name: 'Commissions', href: '/beef/commissions', icon: BarChart3, roles: ['admin', 'manager'] },
  { name: 'Reports', href: '/beef/reports', icon: ClipboardList, roles: ['admin', 'manager'] },
];

function RoleBadge({ role }: { role: string }) {
  const colors: Record<string, string> = {
    admin: 'bg-red-100 text-red-700',
    manager: 'bg-harvest-100 text-harvest-800',
    client: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <span className={clsx(
      'inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wider',
      colors[role] || 'bg-gray-100 text-gray-700',
    )}>
      {role}
    </span>
  );
}

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { isExperimental, toggleExperimental } = useExperimentalStore();
  const { activeProduct } = useProductStore();
  // Experimental UX (the toggle, experimental screens and chatbot) is staff
  // only: admins and managers. Customers never see any of it.
  const canExperiment = user?.role === 'admin' || user?.role === 'manager';
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = activeProduct === 'beef' ? beefNavigation : grainNavigation;

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip to content */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-gold focus:text-white focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Skip to content
      </a>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 w-64 bg-white sidebar-dark border-r border-gray-200 z-50 transform transition-transform duration-300 ease-in-out flex flex-col',
          'lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Brand Header */}
        <div className="relative px-4 py-4 border-b-2 border-brand-gold">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="h-14 overflow-hidden flex items-center">
              <img
                src={isDark ? '/images/bushel-view-logo-light.png' : '/images/bushel-view-logo.png'}
                alt="Bushel View"
                className="w-auto max-w-[220px] scale-[1.4]"
              />
            </div>
          </Link>
          {/* Close button - mobile only */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-3 right-3 p-1.5 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Product Switcher - hidden until Beef View is ready for production */}
        {/* <div className="px-3 pt-3">
          <ProductSwitcher />
        </div> */}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {navigation
            .filter((item) => !item.roles || (user?.role && item.roles.includes(user.role)))
            .filter((item) => !item.experimental || isExperimental)
            .map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={clsx(
                  'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 relative',
                  isActive
                    ? 'bg-harvest-50 text-harvest-800'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                {/* Active indicator */}
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-brand-gold rounded-r-full" />
                )}
                <Icon
                  className={clsx(
                    'w-[18px] h-[18px] mr-3 flex-shrink-0 transition-colors',
                    isActive ? 'text-brand-gold' : 'text-gray-400 group-hover:text-gray-600'
                  )}
                />
                <span className="flex-1">{item.name}</span>
                {item.experimental && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
                    V2
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Experimental UX Toggle - staff only (admins and managers) */}
        {canExperiment && (
          <div className="px-4 py-3 border-t border-gray-200">
            <button
              onClick={toggleExperimental}
              className="flex items-center justify-between w-full group"
              title={isExperimental ? 'Switch to classic view' : 'Switch to experimental view'}
            >
              <span className="flex items-center gap-2 text-xs font-medium text-gray-500 group-hover:text-gray-700 transition-colors">
                <FlaskConical className={clsx('w-3.5 h-3.5', isExperimental ? 'text-brand-gold' : 'text-gray-400')} />
                Experimental UX
              </span>
              <div className={clsx(
                'relative w-9 h-5 rounded-full transition-colors duration-200',
                isExperimental ? 'bg-brand-gold' : 'bg-gray-300'
              )}>
                <div className={clsx(
                  'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200',
                  isExperimental && 'translate-x-4'
                )} />
              </div>
            </button>
          </div>
        )}

        {/* User section */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-gold to-harvest-600 flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {user?.firstName} {user?.lastName}
              </p>
              {user?.role && <RoleBadge role={user.role} />}
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-400 hover:text-harvest-500 rounded-lg hover:bg-gray-100 transition-all"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-all"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white sidebar-dark border-b border-gray-200 z-30 flex items-center px-4 shadow-sm">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Open sidebar menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex-1 flex justify-center">
          <img
            src={isDark ? '/images/bushel-view-logo-light.png' : '/images/bushel-view-logo.png'}
            alt="Bushel View"
            className="h-9 w-auto"
          />
        </div>
        <div className="w-10" /> {/* Spacer for centering */}
      </header>

      {/* Main content */}
      <main id="main-content" className="lg:pl-64 pt-16 lg:pt-0">
        {isExperimental && canExperiment && (
          <div className="hidden sm:block sticky top-0 lg:top-0 z-20">
            <PriceTicker />
          </div>
        )}
        <div className="py-4 px-4 sm:py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      {/* Floating ChatBot (experimental, staff only) */}
      {isExperimental && canExperiment && <ChatBot />}
    </div>
  );
}
